import { ArrowLeft, CalendarClock, GraduationCap, Link2, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { createClassJoin, getClass, getPublicAcademyBySlug } from '../../firebase/firestore';
import { formatClassSchedule } from '../../utils/classSchedule';
import { applyPageMetadata } from '../../utils/pageMetadata';
import { isEmail } from '../../utils/validators';

const DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
};

export function PublicClassPage() {
  const { slug, classId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [classItem, setClassItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState('');
  const [values, setValues] = useState(DEFAULT_VALUES);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      const academy = await getPublicAcademyBySlug(slug);
      if (!academy) {
        if (mounted) {
          setTenant(null);
          setLoading(false);
        }
        return;
      }

      const nextClass = await getClass(academy.id, classId);
      if (mounted) {
        setTenant(academy);
        setClassItem(nextClass);
        setLoading(false);
      }
    }

    load().catch((err) => {
      console.error(err);
      setLoading(false);
    });

    return () => { mounted = false; };
  }, [classId, slug]);

  useEffect(() => {
    if (!tenant || !classItem) return undefined;

    return applyPageMetadata({
      title: `${classItem.title} | ${tenant.name} | Smart Campus Africa`,
      description: `Join the ${classItem.title} class at ${tenant.name} on Smart Campus Africa.`,
      canonicalUrl: window.location.href,
      ogTitle: `${classItem.title} | ${tenant.name}`,
      ogDescription: `Join the ${classItem.title} class at ${tenant.name} on Smart Campus Africa.`,
    });
  }, [classItem, tenant]);

  async function onSubmit(event) {
    event.preventDefault();
    setError('');

    if (!isEmail(values.email)) {
      setError('Enter a valid email address.');
      return;
    }

    setSaving(true);
    try {
      await createClassJoin(tenant.id, classItem.id, values);
      setJoined(true);
    } catch (err) {
      console.error(err);
      setError('Unable to save your class login. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState label="Loading class..." />;

  if (!tenant || !classItem || classItem.publicAccess?.enabled === false) {
    return (
      <main className="academy-public">
        <section className="academy-hero compact">
          <Link to="/" className="academy-back"><ArrowLeft size={18} /> SmartCampus</Link>
          <EmptyState title="Class not found" message="This class login link is not available." actionLabel="Back Home" to="/" icon={GraduationCap} />
        </section>
      </main>
    );
  }

  const meetingUrl = classItem.meetingUrl || '';

  return (
    <main className="academy-public">
      <section className="academy-hero compact">
        <Link to={`/academy/${tenant.slug}`} className="academy-back"><ArrowLeft size={18} /> Back to academy</Link>
        <div className="academy-avatar">{tenant.name.charAt(0).toUpperCase()}</div>
        <p className="eyebrow">{tenant.type?.replace(/_/g, ' ') || 'Academy'}</p>
        <h1>{classItem.title}</h1>
        <p>Students can log in here every day for the continuous class session at the same time.</p>
      </section>

      <section className="academy-course-section">
        <div className="section-heading">
          <h2>Class Access</h2>
          <p>{tenant.name}</p>
        </div>
        <div className="signup-success">
          <div className="course-meta-row">
            <strong><CalendarClock size={14} /> {formatClassSchedule(classItem)}</strong>
            <strong><GraduationCap size={14} /> {classItem.instructorName || 'Instructor pending'}</strong>
            <strong><Link2 size={14} /> {classItem.publicAccess?.label || 'Student login'}</strong>
          </div>
          <p>This is a continuous daily class. Once you log in, you can return to the same link each day for the live session.</p>
          {meetingUrl ? (
            <a className="btn btn-primary" href={meetingUrl} target="_blank" rel="noreferrer">Open Class Link</a>
          ) : null}
        </div>
      </section>

      <section className="academy-course-section">
        <div className="section-heading">
          <h2>Log in to class</h2>
          <p>Enter your details so the academy can see who has joined the class.</p>
        </div>
        {joined ? (
          <div className="signup-success">
            <p>Thanks, {values.firstName}. Your class login has been recorded for {classItem.title}.</p>
            {meetingUrl ? (
              <a className="btn btn-primary" href={meetingUrl} target="_blank" rel="noreferrer">
                <Send size={18} aria-hidden="true" />
                <span>Join Class Now</span>
              </a>
            ) : null}
          </div>
        ) : (
          <section className="panel">
            <form onSubmit={onSubmit} className="modal-form">
              {error ? <div className="form-error">{error}</div> : null}
              <div className="form-grid">
                <FormField label="First Name" id="class-first-name">
                  <input id="class-first-name" required value={values.firstName} onChange={(event) => setValues({ ...values, firstName: event.target.value })} />
                </FormField>
                <FormField label="Last Name" id="class-last-name">
                  <input id="class-last-name" required value={values.lastName} onChange={(event) => setValues({ ...values, lastName: event.target.value })} />
                </FormField>
              </div>
              <FormField label="Email" id="class-email">
                <input id="class-email" type="email" required value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} />
              </FormField>
              <FormField label="Phone" id="class-phone">
                <input id="class-phone" value={values.phone} onChange={(event) => setValues({ ...values, phone: event.target.value })} />
              </FormField>
              <FormField label="Message" id="class-message">
                <textarea id="class-message" value={values.message} onChange={(event) => setValues({ ...values, message: event.target.value })} placeholder="Optional note" />
              </FormField>
              <div className="form-actions">
                <Button type="submit" loading={saving}>Log In</Button>
              </div>
            </form>
          </section>
        )}
      </section>
    </main>
  );
}
